import { MoveStrategy } from './MoveStrategy';
import { Position, BoardState } from '../model/BoardState';
import { CaptureMoveDetector } from './CaptureMoveDetector';
import logger from '../utils/Logger';

export class ComputerRandomMoveStrategy implements MoveStrategy {
  getLegalMoves(_position: Position | null, boardState: BoardState): Position[] {
    const computerPieces = boardState.pieces.filter(piece => piece.color === 'white');
    const legalMoves: Position[] = [];
    const captureMoves: Position[] = [];

    // Collect all legal moves and separate capture moves
    for (const piece of computerPieces) {
      const moves = boardState.getLegalMoves(piece).filter(this.isWithinBounds);
      
      for (const move of moves) {
        move.piece = piece;
        
        // Check if it's a capture move
        if (CaptureMoveDetector.isCaptureMove(move, boardState, 'white')) {
          captureMoves.push(move);
        } else {
          legalMoves.push(move);
        }
      }
    }

    // If there are capture moves, prioritize them
    if (captureMoves.length > 0) {
      // Sort by value and pick one of the top moves randomly
      const sortedCaptures = CaptureMoveDetector.prioritizeCaptureMoves(captureMoves, boardState);
      const topCaptures = sortedCaptures.slice(0, Math.min(3, sortedCaptures.length)); // Take top 3 captures
      
      // Choose the highest-value capture for consistency in production
    // but for tests, we'll use random selection
    const selectedMove = topCaptures[0];
      
      // Look for what piece is being captured for logging
      if (boardState && boardState.pieces && Array.isArray(boardState.pieces)) {
        const capturedPiece = boardState.pieces.find(p => 
          p.position.row === selectedMove.row && 
          p.position.col === selectedMove.col && 
          p.color !== 'white'
        );
        
        if (capturedPiece) {
          logger.debug(`RandomMoveStrategy selecting capture move: Piece ${selectedMove.piece!.type} capturing ${capturedPiece.type} at (${selectedMove.row}, ${selectedMove.col})`);
          logger.debug(`Computer (RandomMoveStrategy) will capture ${capturedPiece.type} with ${selectedMove.piece!.type} at position (${selectedMove.row}, ${selectedMove.col})`);
        } else {
          logger.debug(`RandomMoveStrategy selecting capture move: Piece ${selectedMove.piece!.type} capturing at (${selectedMove.row}, ${selectedMove.col})`);
          logger.debug(`Computer (RandomMoveStrategy) will capture with ${selectedMove.piece!.type} at position (${selectedMove.row}, ${selectedMove.col})`);
        }
      } else {
        logger.debug(`RandomMoveStrategy selecting capture move: Piece ${selectedMove.piece!.type} capturing at (${selectedMove.row}, ${selectedMove.col})`);
        logger.debug(`Computer (RandomMoveStrategy) will capture with ${selectedMove.piece!.type} at position (${selectedMove.row}, ${selectedMove.col})`);
      }
      
      return [selectedMove];
    }

    // If no captures are available, make a random move as before
    if (legalMoves.length === 0) {
      return [];
    }
    
    const randomIndex = Math.floor(Math.random() * legalMoves.length);
    return [legalMoves[randomIndex]];
  }

  getStrategyName(): string {
    return "Computer Random Move Strategy";
  }

  private isWithinBounds(position: Position): boolean {
    return position.row >= 0 && position.row < 8 && position.col >= 0 && position.col < 8;
  }
}
